import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Bell, CreditCard, HelpCircle, LogOut, Map, Plus, Settings, User } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function useUnread() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setN(r.data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return n;
}

const navCls = ({ isActive }) => `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-orange-100 text-orange-700" : "text-slate-600 hover:bg-slate-100"}`;

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const unread = useUnread();
  const doLogout = async () => { await logout(); nav("/"); };
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/dashboard" data-testid="nav-home-link"><Logo /></Link>
          <nav className="hidden items-center gap-1 md:flex">
            <NavLink to="/dashboard" className={navCls} data-testid="nav-trips-link">My trips</NavLink>
            <NavLink to="/billing" className={navCls} data-testid="nav-billing-link">Plans</NavLink>
            <NavLink to="/help" className={navCls} data-testid="nav-help-link">Help</NavLink>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/notifications" className="relative rounded-full p-2 hover:bg-slate-100" aria-label="Notifications" data-testid="nav-notifications-button">
              <Bell className="h-5 w-5 text-slate-700" />
              {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white" data-testid="nav-unread-count">{unread}</span>}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-teal-600 text-sm font-bold text-white" data-testid="nav-account-menu">
                {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name || "?")[0].toUpperCase()}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-sm"><p className="font-semibold">{user?.name}</p><p className="truncate text-xs text-slate-500">{user?.email}</p></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings"><Settings className="mr-2 h-4 w-4" />Account settings</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/billing")} data-testid="menu-billing"><CreditCard className="mr-2 h-4 w-4" />Plan & billing</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/help")} data-testid="menu-help"><HelpCircle className="mr-2 h-4 w-4" />Help & support</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-slate-200 bg-white/95 backdrop-blur md:hidden" data-testid="mobile-bottom-nav">
        {[["/dashboard", Map, "Trips"], ["/trips/new", Plus, "New trip"], ["/notifications", Bell, "Alerts"], ["/settings", User, "Account"]].map(([to, Icon, label]) => (
          <NavLink key={to} to={to} end className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-semibold ${isActive ? "text-orange-600" : "text-slate-500"}`} data-testid={`mobile-nav-${label.toLowerCase().replace(" ", "-")}`}>
            <Icon className="h-5 w-5" />{label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
